import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Package, Calendar, LayoutDashboard, Loader2 } from "lucide-react";

const StorageOnboardingWelcome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [boxSlots, setBoxSlots] = useState(0);
  const [onboardingStatus, setOnboardingStatus] = useState<string>("");

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please check your email for the login link",
          variant: "destructive"
        });
        navigate("/auth");
        return;
      }

      setUser(user);

      const { data: profile } = await supabase
        .from('profiles')
        .select('box_slots_purchased, onboarding_status')
        .eq('id', user.id)
        .single();

      if (profile) {
        const slots = profile.box_slots_purchased || parseInt(searchParams.get("box_slots") || "0");
        setBoxSlots(slots);
        setOnboardingStatus(profile.onboarding_status || "pending_setup");

        // Redirect logic based on status
        if (profile.onboarding_status === 'complete') {
          navigate('/dashboard');
          return;
        }
        if (profile.onboarding_status === 'address_confirmed') {
          navigate('/onboarding/express-pickup?step=pickup');
          return;
        }
      }
    } catch (error) {
      console.error('Error checking user status:', error);
      toast({
        title: "Error",
        description: "Failed to load your information",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExpressPath = () => {
    navigate(`/onboarding/express-pickup?step=address&box_slots=${boxSlots}`);
  };

  const handleDashboardPath = async () => {
    if (!user) return;

    try {
      await supabase
        .from('profiles')
        .update({ onboarding_status: 'skipped' })
        .eq('id', user.id);

      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating status:', error);
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const monthlyCost = (boxSlots * 9.99).toFixed(2);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b">
        <div className="container mx-auto px-4 py-4">
          <Package className="h-6 w-6 text-primary inline mr-2" />
          <span className="text-xl font-bold">Endless Storage</span>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold mb-3">Welcome to Endless Storage!</h1>
          <p className="text-xl text-muted-foreground mb-2">
            You purchased <span className="font-semibold text-primary">{boxSlots} box slots</span> for{" "}
            <span className="font-semibold text-primary">${monthlyCost}/month</span>
          </p>
          <p className="text-lg text-muted-foreground">
            How would you like to proceed?
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Express Path */}
          <Card className="border-2 hover:border-primary transition-all cursor-pointer shadow-elegant hover:shadow-lg group"
                onClick={handleExpressPath}>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Calendar className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Schedule Pickup Now</CardTitle>
              <CardDescription className="text-base">
                I have my boxes ready - let's schedule pickup!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Confirm your pickup address</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Choose your pickup date & time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>We'll create your boxes automatically</span>
                </li>
              </ul>
              <Button className="w-full" size="lg">
                Let's Go <Calendar className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-3">Takes 2 minutes</p>
            </CardContent>
          </Card>

          {/* Dashboard Path */}
          <Card className="border-2 hover:border-primary transition-all cursor-pointer shadow-elegant hover:shadow-lg group"
                onClick={handleDashboardPath}>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                <LayoutDashboard className="h-8 w-8 text-secondary-foreground" />
              </div>
              <CardTitle className="text-2xl">Go to Dashboard</CardTitle>
              <CardDescription className="text-base">
                I'll organize my boxes first
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Create boxes at your own pace</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Add photos and inventory items</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Schedule pickup when ready</span>
                </li>
              </ul>
              <Button variant="outline" className="w-full" size="lg">
                Take Me There <LayoutDashboard className="ml-2 h-4 w-4" />
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-3">No rush - schedule pickup anytime</p>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>Either way, your first pickup is <span className="font-semibold">FREE</span></p>
          <p className="mt-1">Billing starts only after your boxes arrive at our secure facility</p>
        </div>
      </div>
    </div>
  );
};

export default StorageOnboardingWelcome;
